import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, Heart, Eye, MessageCircle } from 'lucide-react';
import DownloadPaywall from '../components/DownloadPaywall';
import SignupInviteModal from '../components/SignupInviteModal';
import ContributingArtistsDisplay from '../components/ContributingArtistsDisplay';
import RichDescription from '../components/RichDescription';
import MetadataDisplay from '../components/MetadataDisplay';
import trackingService from '../services/tracking';
import { useAuth } from '../context/AuthContext';
import { updateVideoMeta } from '../utils/metaUtils';
import api from '../services/api';

const VideoViewer = () => {
    const { uploadId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [video, setVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, video: null });
    const [playCount, setPlayCount] = useState(0);
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'video' });
    const videoRef = useRef(null);

    useEffect(() => {
        const fetchVideo = async () => {
            try {
                console.log('Fetching video with ID:', uploadId);
                
                // Get all public video uploads and find by ID
                const response = await api.get('/upload/public/video');
                console.log('API response status:', response.status);
                
                const result = response.data;
                console.log('API result:', result);
                
                if (result.success && result.data && result.data.length > 0) {
                    const videoData = result.data.find(upload => upload.id == uploadId);
                    console.log('Found video data:', videoData);
                    
                    if (videoData) {
                        setVideo(videoData);
                        setPlayCount(videoData.play_count || 0);
                        
                        // Update page meta tags for SEO and social sharing
                        updateVideoMeta(videoData).catch(console.error);
                        
                        // Track page view
                        trackingService.trackPageView('video', uploadId);
                    } else {
                        setError(`Video with ID ${uploadId} not found in ${result.data.length} videos`);
                    }
                } else {
                    setError('No public videos available');
                }
            } catch (err) {
                console.error('Error fetching video:', err);
                setError(`Failed to load video: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        const fetchComments = async () => {
            try {
                setLoadingComments(true);
                const response = await api.get(`/upload/${uploadId}/comments`);
                
                if (response.data.success) {
                    setComments(response.data.data);
                }
            } catch (err) {
                console.error('Error fetching comments:', err);
            } finally {
                setLoadingComments(false);
            }
        };

        if (uploadId) {
            fetchVideo();
            fetchComments();
        }
    }, [uploadId]);

    const handleDownloadClick = () => {
        setDownloadPaywall({ isOpen: true, video });
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: video.title,
                    text: video.description || 'Check out this video on The Deep Wave',
                    url: window.location.href
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(window.location.href);
        }
    };

    const handleLike = async () => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'video' });
            return;
        }

        try {
            const response = await api.post(`/upload/${uploadId}/like`);
            
            if (response.data.success) {
                setVideo(prev => ({
                    ...prev,
                    like_count: response.data.data.likeCount,
                    user_has_liked: response.data.data.liked
                }));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const handleCommentLike = async (commentId) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'video' });
            return;
        }

        try {
            const response = await api.post(`/upload/comments/${commentId}/like`);
            
            if (response.data.success) {
                setComments(prev => prev.map(comment => 
                    comment.id === commentId
                        ? { ...comment, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : comment
                ));
            }
        } catch (err) {
            console.error('Error toggling comment like:', err);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !isAuthenticated) return;

        try {
            setSubmittingComment(true);
            const response = await api.post(`/upload/${uploadId}/comments`, {
                content: newComment.trim()
            });
            
            if (response.data.success) {
                setComments(prev => [response.data.data, ...prev]);
                setNewComment('');
                setVideo(prev => ({
                    ...prev,
                    comment_count: (prev.comment_count || 0) + 1
                }));
            }
        } catch (err) {
            console.error('Error adding comment:', err);
        } finally {
            setSubmittingComment(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
        return date.toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-lg">Loading video...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-400 text-lg mb-4">{error}</div>
                    <button 
                        onClick={() => navigate('/')}
                        className="text-purple-400 hover:text-purple-300"
                    >
                        Go back to home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Header */}
            <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 p-4">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                    </button>
                    
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleLike}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                video?.user_has_liked
                                    ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                    : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            title={isAuthenticated 
                                ? (video?.user_has_liked ? 'Unlike this video' : 'Like this video')
                                : 'Sign up to like videos and support creators'
                            }
                        >
                            <Heart className={`w-4 h-4 ${video?.user_has_liked ? 'fill-current' : ''}`} />
                            {video?.like_count || 0}
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Share"
                        >
                            <Share2 className="w-4 h-4" />
                            Share
                        </button>
                        <button
                            onClick={handleDownloadClick}
                            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Download
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-6xl mx-auto p-6">
                {/* Video Player */}
                <div className="mb-8">
                    <video
                        ref={videoRef}
                        src={video?.file_url}
                        className="w-full max-h-[80vh] bg-black rounded-lg shadow-2xl"
                        controls
                        controlsList="nodownload"
                        autoPlay
                        preload="metadata"
                        playsInline
                        onPlay={() => {
                            trackingService.startPlayTracking(uploadId, videoRef.current);
                            setPlayCount(prev => prev + 1);
                        }}
                        onPause={() => {
                            trackingService.stopPlayTracking(uploadId);
                        }}
                    />
                </div>

                {/* Video Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column - Description */}
                    <div className="space-y-6">
                        <div className="bg-gray-800/50 rounded-lg p-6">
                            <h1 className="text-3xl font-bold mb-4">{video?.title}</h1>
                            <h3 className="text-lg font-semibold text-gray-300 mb-2">Description</h3>
                            <RichDescription
                                description={video?.description}
                                maxLength={400}
                                showLabel={false}
                                textClassName="text-gray-300"
                            />
                        </div>
                        
                        {/* Contributing Artists */}
                        {video?.id && (
                            <ContributingArtistsDisplay
                                mediaType="upload"
                                mediaId={video.id}
                                layout="horizontal"
                                maxVisible={5}
                                className="bg-gray-800/50 rounded-lg p-6"
                            />
                        )}
                    </div>
                    
                    {/* Right Column - Details */}
                    <div className="bg-gray-800/50 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-gray-300">Details</h3>
                        </div>
                        
                        <div className="space-y-3 text-sm">
                            {/* Basic File Info */}
                            <div className="flex justify-between">
                                <span className="text-gray-400">Type:</span>
                                <span className="text-white">Video</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Size:</span>
                                <span className="text-white">{video?.file_size ? (video.file_size / 1024 / 1024).toFixed(1) + ' MB' : '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Format:</span>
                                <span className="text-white">{video?.mime_type?.split('/')[1]?.toUpperCase() || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Uploaded:</span>
                                <span className="text-white">{video?.created_at ? new Date(video.created_at).toLocaleDateString() : '-'}</span>
                            </div>
                            
                            {/* Video Resolution */}
                            {video?.width && video?.height && (
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Resolution:</span>
                                    <span className="text-white">{video.width} × {video.height}</span>
                                </div>
                            )}
                            
                            {/* Separator */}
                            <div className="border-t border-gray-700 my-4"></div>
                            
                            {/* Video Metadata */}
                            <div className="flex justify-between">
                                <span className="text-gray-400">Creator:</span>
                                <span className="text-white">{video?.creator || video?.artist || video?.display_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Category:</span>
                                <span className="text-white">{video?.category || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Year:</span>
                                <span className="text-white">{video?.year || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Duration:</span>
                                <span className="text-white">{video?.duration ? Math.floor(video.duration / 60) + ':' + (video.duration % 60).toString().padStart(2, '0') : '-'}</span>
                            </div>
                            
                            {/* Tags */}
                            {video?.tags && (
                                <div>
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-400">Tags:</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {(typeof video.tags === 'string' ? JSON.parse(video.tags) : video.tags).slice(0, 5).map((tag, index) => (
                                            <span
                                                key={index}
                                                className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full"
                                            >
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Separator */}
                            <div className="border-t border-gray-700 my-4"></div>
                            
                            {/* Engagement Stats */}
                            <div className="flex justify-between">
                                <span className="text-gray-400">Views:</span>
                                <span className="text-white">{playCount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Likes:</span>
                                <span className="text-white">{video?.like_count || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Comments:</span>
                                <span className="text-white">{video?.comment_count || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Visibility:</span>
                                <span className="text-white">{video?.is_public ? 'Public' : 'Private'}</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Creator Profile Section */}
                <div className="bg-gray-800/50 rounded-lg p-6 mb-8">
                    <h3 className="font-semibold mb-4 text-gray-300">Creator</h3>
                    <div className="flex items-center gap-4">

                        <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center">
                            {video?.profile_picture_url ? (
                                <img
                                    src={video.profile_picture_url}
                                    alt={video.display_name}
                                    width="64"
                                    height="64"
                                    className="w-16 h-16 rounded-full object-cover"
                                />
                            ) : (
                                <span className="text-white font-semibold text-xl">
                                    {video?.display_name?.charAt(0) || video?.username?.charAt(0) || 'U'}
                                </span>
                            )}
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-lg">{video?.display_name || video?.username}</p>
                            <p className="text-gray-400">@{video?.username}</p>
                        </div>
                        <div className="flex items-center gap-6">
                            <button
                                onClick={handleLike}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                    video?.user_has_liked
                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                }`}
                            >
                                <Heart className={`w-5 h-5 ${video?.user_has_liked ? 'fill-current' : ''}`} />
                                <span>{video?.like_count || 0}</span>
                            </button>

                            <div className="flex items-center gap-2 text-gray-400">
                                <MessageCircle className="w-5 h-5" />
                                <span>{video?.comment_count || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comments Section */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-white mb-6">
                        Comments ({video?.comment_count || 0})
                    </h3>

                    {/* Add Comment Form */}
                    {isAuthenticated ? (
                        <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                            <form onSubmit={handleAddComment}>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-4 text-white placeholder-gray-400 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                    rows="3"
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="submit"
                                        disabled={!newComment.trim() || submittingComment}
                                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        {submittingComment ? 'Posting...' : 'Post Comment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="bg-gray-800/50 rounded-lg p-6 mb-6 text-center">
                            <div className="text-4xl mb-4">💬</div>
                            <h3 className="text-xl font-bold text-white mb-2">Join the conversation</h3>
                            <p className="text-gray-400 mb-6">
                                Login to share your thoughts and connect with other members of The Deep Wave community.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                                >
                                    Login to Comment
                                </button>
                                <button
                                    onClick={() => navigate('/register')}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                                >
                                    Create Account
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Comments List */}
                    <div className="space-y-4">
                        {loadingComments ? (
                            <div className="text-center py-8">
                                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading comments...</p>
                            </div>
                        ) : comments.length > 0 ? (
                            comments.map((comment) => (
                                <div key={comment.id} className="bg-gray-800/30 rounded-lg p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                                            {comment.avatar ? (
                                                <img
                                                    src={comment.avatar}
                                                    alt={comment.display_name}
                                                    width="40"
                                                    height="40"
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <span className="text-white font-semibold text-sm">
                                                    {(comment.display_name || comment.username)?.charAt(0) || 'U'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-white">
                                                    {comment.display_name || comment.username}
                                                </h4>
                                                <span className="text-sm text-gray-500">
                                                    {formatTimeAgo(comment.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-gray-300 leading-relaxed mb-3">
                                                {comment.content}
                                            </p>
                                            <button
                                                onClick={() => handleCommentLike(comment.id)}
                                                className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors text-sm ${
                                                    comment.user_has_liked
                                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                                }`}
                                            >
                                                <Heart className={`w-4 h-4 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                                <span>{comment.like_count || 0}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400">No comments yet. Be the first to comment!</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Download Paywall Modal */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, video: null })}
                mediaTitle={downloadPaywall.video?.title || 'Unknown Video'}
                mediaType="video"
                uploadId={downloadPaywall.video?.id}
            />

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'video' })}
                contentType={signupInvite.contentType}
            />
        </div>
    );
};

export default VideoViewer;